import { get } from '@/api/ApiCalls';
import { BanksResponse } from '@/models/common/BankData';

let banksCache: BanksResponse | null = null;

const fetchBanks = async (): Promise<BanksResponse> => {
  if (banksCache) {
    return banksCache;
  }

  const data = await get<BanksResponse>('banks');
  banksCache = data;
  return data;
};

// Create stable function references by defining them outside
const getBankOptions = async (): Promise<string[]> => {
  const banks = await fetchBanks();
  return Object.entries(banks)
    .map(([id, bank]) => `${id} - ${bank.name}`)
    .sort((a, b) => {
      const idA = parseInt(a.split(' - ')[0]);
      const idB = parseInt(b.split(' - ')[0]);
      return idA - idB;
    });
};

const getBranchOptions = async (bankNumberStr?: string): Promise<string[]> => {
  if (!bankNumberStr) {
    return [];
  }

  // Extract bank number from the format "12 - Bank Name"
  const bankNumber = parseInt(bankNumberStr.split(' - ')[0]);

  if (isNaN(bankNumber)) {
    return [];
  }

  const banks = await fetchBanks();
  const bank = banks[bankNumber];

  if (!bank || !bank.branches) {
    return [];
  }

  return bank.branches
    .map((branch) => `${branch.id} - ${branch.name}`)
    .sort((a, b) => {
      const idA = parseInt(a.split(' - ')[0]);
      const idB = parseInt(b.split(' - ')[0]);
      return idA - idB;
    });
};

// Helper to get the display value for a bank number
const getBankDisplayValue = async (bankNumber: number | string): Promise<string> => {
  if (!bankNumber || bankNumber === 0) {
    return '';
  }

  const banks = await fetchBanks();
  const bankNum = typeof bankNumber === 'string' ? parseInt(bankNumber) : bankNumber;
  const bank = banks[bankNum];

  return bank ? `${bankNum} - ${bank.name}` : '';
};

// Helper to get the display value for a branch number
const getBranchDisplayValue = async (bankNumber: number | string, branchNumber: string): Promise<string> => {
  if (!bankNumber || !branchNumber) {
    return '';
  }

  const banks = await fetchBanks();
  const bankNum = typeof bankNumber === 'string' ? parseInt(bankNumber) : bankNumber;
  const bank = banks[bankNum];

  if (!bank || !bank.branches) {
    return '';
  }

  const branchNum = parseInt(branchNumber);
  const branch = bank.branches.find((b) => b.id === branchNum);

  return branch ? `${branch.id} - ${branch.name}` : '';
};

// Export stable references
const useBanks = {
  getBanks: fetchBanks,
  getBankOptions,
  getBranchOptions,
  getBankDisplayValue,
  getBranchDisplayValue,
};

export default useBanks;
